/**
 * 周期与日历的**中文名唯一源**（F 域 · 端侧）
 *
 * 依据：C 域/端侧既有纪律「中文名只写一处」（`features/journal-labels.ts` 同范式）·
 * `AC-F2.5`（当天 / 逾期文案）· `AC-F2.6`（每项显示类型标签）· F 域 PRD §4.6.2。
 *
 * ## 🔴 为什么文案不放在服务端
 *
 * 服务端下发的是**事实**（`status` / `daysUntil` / `kind`），端侧负责**措辞**。
 * 若把中文也塞进响应，同一个枚举就会在服务端与端侧各有一份文案表 —— 而"两份文案"
 * 迟早会分叉（本项目已登记过同类病灶：`kindLabel` 类的字段越多，改文案要动的地方越多）。
 * ⚠️ 例外：`ScheduleView.categoryName` 之类**由内核派生**的名字仍由服务端下发 —— 那不是措辞，是数据。
 */

import { ScheduleFrequency, ScheduleKind, UpcomingStatus, type UpcomingItemView, type UpcomingKind } from '@jiaying/shared';

/** 必付三项的类型标签（`AC-F2.6`） */
export const KIND_LABELS: Readonly<Record<UpcomingKind, string>> = {
  creditCard: '信用卡',
  payable: '待缴',
  schedule: '周期账',
};

/** 贷款期次的类型标签（批 80 · `AC-F2.6` 三类标签：周期账 / 信用卡 / **贷款**） */
export const LOAN_KIND_LABEL = '贷款';

/**
 * 单个必付项的类型标签 —— 🔴 **唯一取标签入口**（页面不直接读 `KIND_LABELS[item.kind]`）：
 * `subKind === 'loan'`（贷款计划表期次，批 80 起）⇒ 显示「贷款」；否则按三构成默认标签。
 */
export function kindLabelOf(item: Pick<UpcomingItemView, 'kind' | 'subKind'>): string {
  return item.subKind === 'loan' ? LOAN_KIND_LABEL : (KIND_LABELS[item.kind] ?? '');
}

/**
 * 周期规则种类（`schedules.kind`）—— 与 `@jiaying/shared` 的枚举一一对应
 *
 * 🆕 `template`（批 `132` · `DR-074`）：文案用「周期记账模板」——
 * 🔴 **不写「周期账」**（那是 `recurring` 待缴的旧名），两者在页面上必须一眼可分：
 * 前者到期**直接记一笔已发生的收支**，后者生成**待缴**并等销账。
 */
export const SCHEDULE_KIND_LABELS: Readonly<Record<ScheduleKind, string>> = {
  recurring: '周期账',
  installment: '分期',
  loan: '贷款',
  amortization: '摊销',
  template: '周期记账模板',
};

/** 频率（规格 §18 的 5 种） */
export const FREQUENCY_LABELS: Readonly<Record<ScheduleFrequency, string>> = {
  daily: '每天',
  weekly: '每周',
  monthly: '每月',
  quarterly: '每季',
  yearly: '每年',
};

/**
 * 到期文案（`AC-F2.5`）。
 *
 * 🔴 **只消费服务端下发的 `status` / `daysUntil`** —— 不在端侧用 `new Date()` 重算：
 * "今天"是**账本时区**的概念，端侧时区可能不同（且小程序与 Web 也不同源）。
 *
 * 规则：
 * - `已逾期 N 天`（N ≥ 1；逾期是**事实陈述**，不用"警告"措辞 —— 颜色才承担提醒）
 * - `今天到期`
 * - `明天`
 * - `N 天后`（2 ≤ N ≤ 30）
 * - 更远 ⇒ 直接给日期（窗口本身是 30 天，正常到不了这一档，留着防脏数据）
 */
export function dueTextOf(item: Pick<UpcomingItemView, 'status' | 'daysUntil' | 'dueDate'>): string {
  if (item.status === UpcomingStatus.Overdue) return `已逾期 ${-item.daysUntil} 天`;
  if (item.status === UpcomingStatus.Today) return '今天到期';
  if (item.daysUntil === 1) return '明天';
  if (item.daysUntil <= 30) return `${item.daysUntil} 天后`;
  return item.dueDate;
}

/** 合计不完整时的标注（`fxIncomplete` ⇒ 缺汇率的项没进合计，必须说出来） */
export const FX_INCOMPLETE_NOTE = '存在缺少汇率的项目，合计只含可折算的部分';

/** 空态：有引导（`AC-F2.7`）—— 说明"为什么空"并给**真实存在**的下一步 */
export const EMPTY_UPCOMING_TITLE = '近期没有固定支出';
export const EMPTY_UPCOMING_HINT =
  '周期账由「待缴 / 待摊」账户绑定规则生成；信用卡还款日来自卡上的账单日与还款日设置。';
